import { FC, KeyboardEvent, MouseEvent, useState } from 'react';
import { captureException } from '@sentry/react';
import { Button, Col, Input, Modal, Row } from 'antd';
import { TbArrowRight, TbForms, TbX } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { useSubmitShortcut } from '@/hooks/useSubmitShortcut';
import { toast } from '@/shared/components/toasts/PlatformToast';
import { getWorkspaceResourceTypeLabel } from '@/shared/components/workspace-resources';
import {
    useConnectedAccountStore,
    useCustomFilterStore,
    useDashboardStore,
    useFolderStore,
    useInvestmentStore,
    useListStore,
    useListViewColumnStore,
    useListViewStore,
    useMemoStore,
    usePipelineStore,
    useReportComponentStore,
    useReportStore,
    useTeamStore,
} from '@/stores';
import { SetStateMethod } from '@/types';

interface RenameResourceModalProps {
    onRename?: (name: string) => Promise<unknown>;
    resource: any;
    singularResource: string;
    open: boolean;
    setOpen: SetStateMethod<boolean>;
}

const RenameResourceModal: FC<RenameResourceModalProps> = ({
    resource,
    singularResource,
    open,
    setOpen,
    onRename,
}): JSX.Element => {
    const resourceName =
        (resource?.editedName || resource?.title || resource?.name || resource?.label || resource?.nickname) ?? '';
    const resourceTypeLabel = getWorkspaceResourceTypeLabel(singularResource);

    const [input, setInput] = useState(resourceName);
    const [submitting, setSubmitting] = useState(false);

    const { updateFolder, loadingFolder } = useFolderStore();
    const { updateInvestment, loadingInvestment } = useInvestmentStore();
    const { updateMemo, loadingMemo } = useMemoStore();
    const { updatePipeline, loadingPipeline } = usePipelineStore();
    const { updateTeam, loadingTeam } = useTeamStore();
    const { updateCustomFilter, loadingCustomFilter } = useCustomFilterStore();
    const { updateConnectedAccount, loadingConnectedAccount } = useConnectedAccountStore();
    // Relations
    const { updateDashboard, loadingDashboard } = useDashboardStore();
    const { updateReport, loadingReport } = useReportStore();
    const { updateReportComponent, loadingReportComponent } = useReportComponentStore();
    const { updateList, loadingList } = useListStore();
    const { updateListView, loadingListView } = useListViewStore();
    const { updateListViewColumn, loadingListViewColumn } = useListViewColumnStore();

    const nextResourceName = input.trim();

    const functionMap: Record<string, () => Promise<unknown>> = {
        folder: () => updateFolder(resource?.id, { name: nextResourceName }),
        investment: () => updateInvestment(resource?.id, { editedName: nextResourceName }),
        memo: () => updateMemo(resource?.id, { title: nextResourceName }),
        pipeline: () => updatePipeline(resource?.id, { name: nextResourceName }),
        team: () => updateTeam(resource?.id, { name: nextResourceName }),
        'custom-filter': () => updateCustomFilter(resource?.id, { title: nextResourceName }),
        'connected-account': () => updateConnectedAccount(resource?.id, { nickname: nextResourceName }),
        // Relations
        dashboard: () => updateDashboard(resource?.id, { name: nextResourceName }),
        report: () => updateReport(resource?.id, { name: nextResourceName }),
        'report-component': () => updateReportComponent(resource?.id, { name: nextResourceName }),
        list: () => updateList(resource?.id, { name: nextResourceName }),
        'list-view': () => updateListView(resource?.id, { name: nextResourceName }),
        'list-view-column': () => updateListViewColumn(resource?.id, { label: nextResourceName }),
    };

    const errorMap: Record<string, { clear: () => void; get: () => string | undefined }> = {
        folder: {
            clear: () => useFolderStore.setState({ error: undefined }),
            get: () => useFolderStore.getState().error,
        },
        investment: {
            clear: () => useInvestmentStore.setState({ error: undefined }),
            get: () => useInvestmentStore.getState().error,
        },
        memo: { clear: () => useMemoStore.setState({ error: undefined }), get: () => useMemoStore.getState().error },
        pipeline: {
            clear: () => usePipelineStore.setState({ error: undefined }),
            get: () => usePipelineStore.getState().error,
        },
        team: { clear: () => useTeamStore.setState({ error: undefined }), get: () => useTeamStore.getState().error },
        'custom-filter': {
            clear: () => useCustomFilterStore.setState({ error: undefined }),
            get: () => useCustomFilterStore.getState().error,
        },
        'connected-account': {
            clear: () => useConnectedAccountStore.setState({ error: undefined }),
            get: () => useConnectedAccountStore.getState().error,
        },
        dashboard: {
            clear: () => useDashboardStore.setState({ error: undefined }),
            get: () => useDashboardStore.getState().error,
        },
        report: {
            clear: () => useReportStore.setState({ error: undefined }),
            get: () => useReportStore.getState().error,
        },
        'report-component': {
            clear: () => useReportComponentStore.setState({ error: undefined }),
            get: () => useReportComponentStore.getState().error,
        },
        list: { clear: () => useListStore.setState({ error: undefined }), get: () => useListStore.getState().error },
        'list-view': {
            clear: () => useListViewStore.setState({ error: undefined }),
            get: () => useListViewStore.getState().error,
        },
        'list-view-column': {
            clear: () => useListViewColumnStore.setState({ error: undefined }),
            get: () => useListViewColumnStore.getState().error,
        },
    };

    const handleRenameResource = async () => {
        const renameResource = onRename ? () => onRename(nextResourceName) : functionMap[singularResource];
        const operationError = onRename ? undefined : errorMap[singularResource];

        if (nextResourceName !== '' && renameResource && !loading) {
            try {
                setSubmitting(true);
                operationError?.clear();
                await renameResource();

                const error = operationError?.get();

                if (error) {
                    throw new Error(error);
                }

                toast(`${resourceTypeLabel} Renamed`, {
                    icon: <TbForms size='1rem' />,
                    description: nextResourceName,
                });

                setInput('');
                setOpen(false);
            } catch (error) {
                captureException(error);
                toast.error(`Unable to Rename ${resourceTypeLabel}`);
            } finally {
                setSubmitting(false);
            }
        }
    };

    const handleClose = () => {
        if (loading) {
            return;
        }

        setInput(resourceName);
        setOpen(false);
    };

    const disabled =
        resource?.editedName === nextResourceName ||
        resource?.title === nextResourceName ||
        resource?.name === nextResourceName ||
        resource?.label === nextResourceName ||
        resource?.nickname === nextResourceName ||
        nextResourceName === '';

    const loading =
        submitting ||
        loadingFolder ||
        loadingInvestment ||
        loadingMemo ||
        loadingPipeline ||
        loadingDashboard ||
        loadingReport ||
        loadingReportComponent ||
        loadingList ||
        loadingListView ||
        loadingListViewColumn ||
        loadingTeam ||
        loadingCustomFilter ||
        loadingConnectedAccount;

    const { token } = useAntDesignTheme();

    const stopTextInputShortcutPropagation = (event: KeyboardEvent<HTMLInputElement>) => {
        event.stopPropagation();
    };

    const stopModalPropagation = (event: KeyboardEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
        event.stopPropagation();
    };

    useSubmitShortcut(open && !disabled && !loading, handleRenameResource, [input, singularResource, resource?.id]);

    return (
        <>
            <Modal
                afterOpenChange={(nextOpen) => {
                    if (nextOpen) {
                        setInput(resourceName);
                        setSubmitting(false);
                    }
                }}
                open={open}
                mask={false}
                style={{ maxWidth: '25vw' }}
                title={`Rename ${resourceTypeLabel}`}
                closeIcon={<TbX size='1rem' style={{ verticalAlign: '-0.2rem' }} />}
                footer={[
                    <Row key='modalActions' style={{}}>
                        <Col span={12} style={{ textAlign: 'left' }}>
                            <Button onClick={handleClose} disabled={loading}>
                                Cancel
                            </Button>
                        </Col>
                        <Col span={12} style={{ textAlign: 'right' }}>
                            <Button type='primary' disabled={disabled} loading={loading} onClick={handleRenameResource}>
                                Save
                                <TbArrowRight style={{ verticalAlign: '-0.15rem', marginLeft: '0.25rem' }} />
                            </Button>
                        </Col>
                    </Row>,
                ]}
                onOk={handleClose}
                onCancel={handleClose}
                modalRender={(node) => (
                    <div
                        onClick={stopModalPropagation}
                        onKeyDown={stopModalPropagation}
                        onMouseDown={stopModalPropagation}
                    >
                        {node}
                    </div>
                )}
            >
                <Input
                    value={input}
                    size='large'
                    prefix={
                        <TbForms size='1rem' style={{ color: token.colorTextQuaternary, marginRight: '0.375rem' }} />
                    }
                    placeholder={`New ${resourceTypeLabel}...`}
                    minLength={1}
                    maxLength={100}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDownCapture={stopTextInputShortcutPropagation}
                    onKeyUpCapture={stopTextInputShortcutPropagation}
                    onPressEnter={handleRenameResource}
                    style={{ marginTop: '0.5rem' }}
                />
            </Modal>
        </>
    );
};

export default RenameResourceModal;
